import React, { useEffect, useRef, useState } from 'react';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { DateTimeAgoFormat } from '../../helper/Formatters';
import { useUser } from '../../services/redux-services/UserInfoContext';

const AVATAR_COLORS = ['#1e3a5f', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626'];
const getAvatarColor = (str = '') => AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];
const getInitials = (firstName, lastName) =>
    `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';

const CommentsBox = ({ euid }) => {
    const { user } = useUser();
    const api = useApiServices();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const endRef = useRef(null);

    const fetchComments = async () => {
        setLoading(true);
        const { isSuccess, result } = await api.GetCommentAsync(euid);
        if (isSuccess && result) setComments(result);
        setLoading(false);
    };

    const addComment = async () => {
        const content = newComment.trim();
        if (!content) return;
        setSubmitting(true);
        const { isSuccess, result } = await api.AddCommentAsync(euid, { content, replyTo: null, id: null });
        if (isSuccess && result) {
            setComments(prev => [...prev, result]);
            setNewComment('');
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
        setSubmitting(false);
    };

    const handleRemove = async (comment) => {
        if (!window.confirm('ต้องการลบความคิดเห็นนี้หรือไม่?')) return;
        const { isSuccess } = await api.UpdateCommentAsync(euid, { ...comment, isDelete: true });
        if (isSuccess) setComments(prev => prev.filter(c => c.id !== comment.id));
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            addComment();
        }
    };

    useEffect(() => {
        if (euid) fetchComments();
    }, [euid]);

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ความคิดเห็น
                </span>
                {comments.length > 0 && (
                    <span style={{
                        fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5',
                        borderRadius: 10, padding: '1px 7px',
                    }}>{comments.length}</span>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />กำลังโหลด...
                </div>
            ) : comments.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13,
                    background: '#f9fafb', borderRadius: 10, marginBottom: 12,
                }}>
                    <i className="fa fa-comments" style={{ fontSize: 22, marginBottom: 8, display: 'block', opacity: 0.4 }} />
                    ยังไม่มีความคิดเห็น
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14, maxHeight: 380, overflowY: 'auto', paddingRight: 2 }}>
                    {comments.map(c => {
                        const isMe = c.userId === user?.id;
                        const name = isMe ? 'ฉัน' : `${c.user?.firstName || ''} ${c.user?.lastName || ''}`.trim() || 'ผู้ใช้';
                        const initials = isMe
                            ? getInitials(user?.firstName, user?.lastName)
                            : getInitials(c.user?.firstName, c.user?.lastName);
                        const color = getAvatarColor(c.user?.firstName || c.userId || '');

                        return (
                            <div key={c.id} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
                                {/* Avatar */}
                                <div style={{
                                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                    background: isMe
                                        ? 'linear-gradient(135deg, #1e3a5f, #2d6a9f)'
                                        : `linear-gradient(135deg, ${color}cc, ${color})`,
                                    color: '#fff', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: 11, fontWeight: 700,
                                }}>
                                    {initials}
                                </div>

                                {/* Bubble */}
                                <div style={{ maxWidth: '72%' }}>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3, textAlign: isMe ? 'right' : 'left' }}>
                                        {name}
                                    </div>
                                    <div style={{
                                        background: isMe ? 'linear-gradient(135deg, #1e3a5f, #2d6a9f)' : '#f3f4f6',
                                        color: isMe ? '#fff' : '#1f2937',
                                        borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                        padding: '9px 13px', fontSize: 13, lineHeight: 1.55,
                                        wordBreak: 'break-word',
                                    }}>
                                        {c.content}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{DateTimeAgoFormat(c.createDate)}</span>
                                        {isMe && (
                                            <button
                                                onClick={() => handleRemove(c)}
                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10, color: '#f87171' }}
                                            >
                                                ลบ
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={endRef} />
                </div>
            )}

            {/* Input */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 8,
                background: '#f9fafb', borderRadius: 12,
                border: '1.5px solid #e5e7eb', padding: '8px 10px',
            }}>
                <textarea
                    rows={1}
                    placeholder="เขียนความคิดเห็น... (Enter ส่ง, Shift+Enter ขึ้นบรรทัด)"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={handleKey}
                    style={{
                        flex: 1, border: 'none', background: 'transparent', resize: 'none',
                        outline: 'none', fontSize: 13, color: '#1f2937', lineHeight: 1.5,
                        fontFamily: 'inherit', minHeight: 22,
                    }}
                />
                <button
                    onClick={addComment}
                    disabled={!newComment.trim() || submitting}
                    style={{
                        width: 32, height: 32, borderRadius: '50%', border: 'none', flexShrink: 0,
                        background: !newComment.trim() || submitting ? '#e5e7eb' : 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                        color: !newComment.trim() || submitting ? '#9ca3af' : '#fff',
                        cursor: !newComment.trim() || submitting ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                    }}
                >
                    {submitting
                        ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 12 }} />
                        : <i className="fa fa-paper-plane" style={{ fontSize: 12 }} />
                    }
                </button>
            </div>
        </div>
    );
};

export default CommentsBox;
